// src/view/arenaView.ts — ArenaDef → Platformer kit InstancedMesh'leri (model başına bir örnek kümesi: 3×3 platform_6x6x2 + 2 alt
// parça), üst kenar emissive şeridi + kenar uyarı halkası (DESIGN §8 "kenar okunurluğu"). Ölçüler ArenaDef'ten (katalogtan türetilmiş);
// parçalar y = topY − height'a; matrixAutoUpdate kapalı; instanceMatrix.needsUpdate + computeBoundingSphere (ARCHITECTURE §6).
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { ArenaDef, PlatformDef } from '../core/types.ts';
import { T } from '../config/tuning.ts';
import { kitMaterial, loadKitGeometry } from './assets.ts';

/** Şerit ölçüleri (DESIGN §8 "sürekli ince emissive şerit"; sayılar belgede yok → yerel, needsWP0). */
const STRIP_WIDTH = 0.12;
const STRIP_HEIGHT = 0.03;
const STRIP_COLOR = 0x8fd8ff;
/** Kenar uyarı halkası: alarm mesafesi (EDGE.alarmDist 2 u) kadar içeride, soluk kırmızımsı. */
const ALARM_WIDTH = 0.05;
const ALARM_COLOR = 0x5a2a2a;

/** Yürünebilir platformların birleşik AABB'si (3×3 düzende tam dikdörtgen; farklı düzenlerde yaklaşık). */
export function walkableBounds(arena: ArenaDef): { minX: number; maxX: number; minZ: number; maxZ: number; topY: number } {
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, topY = -Infinity;
  for (const p of arena.platforms) {
    if (!p.walkable) continue;
    minX = Math.min(minX, p.minX); maxX = Math.max(maxX, p.maxX); minZ = Math.min(minZ, p.minZ); maxZ = Math.max(maxZ, p.maxZ);
    topY = Math.max(topY, p.topY);
  }
  return { minX, maxX, minZ, maxZ, topY };
}

/** Parça örnek matrisi: merkez XZ, taban y = topY − height (kit min y 0). */
export function platformMatrix(out: THREE.Matrix4, p: PlatformDef): THREE.Matrix4 {
  return out.makeTranslation((p.minX + p.maxX) / 2, p.topY - p.height, (p.minZ + p.maxZ) / 2);
}

/** Dikdörtgen çevresine 4 ince kutu → tek geometri (1 draw call). */
function rectStrip(minX: number, maxX: number, minZ: number, maxZ: number, y: number, w: number, h: number): THREE.BufferGeometry {
  const lx = maxX - minX + w, lz = maxZ - minZ + w;
  const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
  const parts = [
    new THREE.BoxGeometry(lx, h, w).translate(cx, y, minZ),
    new THREE.BoxGeometry(lx, h, w).translate(cx, y, maxZ),
    new THREE.BoxGeometry(w, h, lz).translate(minX, y, cz),
    new THREE.BoxGeometry(w, h, lz).translate(maxX, y, cz),
  ];
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  return merged;
}

export class ArenaView {
  readonly group = new THREE.Group();
  readonly meshes = new Map<string, THREE.InstancedMesh>();
  readonly edgeStrip: THREE.Mesh;
  readonly alarmStrip: THREE.Mesh;
  /** Tüm parçaların birleşik kutusu (gölge frustum'u / kamera tabanı). */
  readonly bounds = new THREE.Box3();

  constructor(readonly arena: ArenaDef, geometries: ReadonlyMap<string, THREE.BufferGeometry>, material: THREE.Material) {
    this.group.name = 'arena';
    // Model başına örnek sayısı
    const counts = new Map<string, number>();
    for (const p of arena.platforms) counts.set(p.kitModel, (counts.get(p.kitModel) ?? 0) + 1);
    const m = new THREE.Matrix4();
    const next = new Map<string, number>();
    for (const [model, n] of counts) {
      const geo = geometries.get(model);
      if (!geo) throw new Error(`arena geometry missing: ${model}`);
      const im = new THREE.InstancedMesh(geo, material, n);
      im.name = 'kit_' + model;
      im.castShadow = true; im.receiveShadow = true;
      im.matrixAutoUpdate = false;
      this.meshes.set(model, im);
      next.set(model, 0);
      this.group.add(im);
    }
    for (const p of arena.platforms) {
      const im = this.meshes.get(p.kitModel)!;
      const i = next.get(p.kitModel)!;
      im.setMatrixAt(i, platformMatrix(m, p));
      next.set(p.kitModel, i + 1);
      this.bounds.expandByPoint(new THREE.Vector3(p.minX, p.topY - p.height, p.minZ));
      this.bounds.expandByPoint(new THREE.Vector3(p.maxX, p.topY, p.maxZ));
    }
    // Matrisler yazıldıktan sonra zorunlu (ARCHITECTURE §6: bayat küre riski; arena statik → bir kez)
    for (const im of this.meshes.values()) { im.instanceMatrix.needsUpdate = true; im.computeBoundingSphere(); }

    // Üst kenar emissive şeridi (unlit MeshBasic = emissive görünüm; toneMapped kapalı → ACES soluklaştırmaz)
    const b = walkableBounds(arena);
    const stripMat = new THREE.MeshBasicMaterial({ color: STRIP_COLOR, toneMapped: false });
    this.edgeStrip = new THREE.Mesh(rectStrip(b.minX, b.maxX, b.minZ, b.maxZ, b.topY + STRIP_HEIGHT / 2, STRIP_WIDTH, STRIP_HEIGHT), stripMat);
    this.edgeStrip.name = 'edgeStrip';
    this.edgeStrip.matrixAutoUpdate = false;
    this.group.add(this.edgeStrip);
    // Kenar uyarı halkası: alarm mesafesi içeride (kendi karakterinde kırmızı zemin halkası bunun üstüne biner — vfx)
    const d = T.EDGE.alarmDist;
    const alarmMat = new THREE.MeshBasicMaterial({ color: ALARM_COLOR, toneMapped: false, transparent: true, opacity: 0.6, depthWrite: false });
    this.alarmStrip = new THREE.Mesh(rectStrip(b.minX + d, b.maxX - d, b.minZ + d, b.maxZ - d, b.topY + 0.006, ALARM_WIDTH, 0.01), alarmMat);
    this.alarmStrip.name = 'alarmStrip';
    this.alarmStrip.matrixAutoUpdate = false;
    this.group.add(this.alarmStrip);
  }

  /** Kit geometrilerini yükleyip görünümü kurar (WP9: `await ArenaView.build(ARENA)` → `stage.scene.add(view.group)`). */
  static async build(arena: ArenaDef): Promise<ArenaView> {
    const models = [...new Set(arena.platforms.map((p) => p.kitModel))];
    const geos = await Promise.all(models.map((id) => loadKitGeometry(id)));
    const map = new Map<string, THREE.BufferGeometry>();
    models.forEach((id, i) => map.set(id, geos[i]!));
    return new ArenaView(arena, map, kitMaterial());
  }

  get instanceCount(): number { let n = 0; for (const im of this.meshes.values()) n += im.count; return n; }

  dispose(): void {
    this.group.removeFromParent();
    for (const im of this.meshes.values()) im.dispose();
    this.edgeStrip.geometry.dispose(); (this.edgeStrip.material as THREE.Material).dispose();
    this.alarmStrip.geometry.dispose(); (this.alarmStrip.material as THREE.Material).dispose();
  }
}
